import { ReactNode, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  question: string;
  answer: ReactNode;
  className?: string;
  defaultOpen?: boolean;
};

/**
 * Opens and closes by animating grid-template-rows between 0fr and 1fr, which is what
 * lets the panel transition to its natural height without measuring it. The answer stays
 * in the DOM either way, so it is findable by in-page search and by crawlers.
 */
export default function FaqAccordion({ question, answer, className, defaultOpen = false }: Props) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-md border-2 border-solid border-slate-900',
        className
      )}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'w-full flex items-center justify-between text-left cursor-pointer bg-white',
          'p-4 lg:p-5 border-0 border-b-2 border-solid transition-colors duration-300',
          isOpen ? 'border-slate-900' : 'border-white'
        )}
      >
        <span className="font-semibold text-lg lg:text-xl mr-3">{question}</span>
        <FontAwesomeIcon
          icon={faPlus as IconProp}
          aria-hidden
          className={cn('text-2xl transition-transform duration-300', isOpen && 'rotate-45')}
        />
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="p-5 w-full bg-white border-t-2 border-slate-900 text-base sm:text-lg">
            <div className="max-w-4xl">{answer}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
